/**
 * How much a frame draws, accumulated from the render passes we observe.
 *
 * Two constraints shape this:
 *
 * 1. **Draw calls are three's, not ours.** They happen inside the renderer,
 *    between our `onBeforeRender` and `onAfterRender` hooks, so we cannot count
 *    them. What we CAN do is read `renderer.info` at the end of each pass and sum.
 *    The two backends report differently — WebGL normally resets `info` per
 *    `render()` but can expose cumulative counters when `autoReset` is false;
 *    WebGPU keeps frame-cumulative counters plus a lifetime `.calls` — so
 *    {@link readRendererInfo} maps both to a {@link NormalizedRenderInfo} and
 *    {@link readPassStats} reduces that to a pass's own work before we sum
 *    (reading WebGPU's lifetime `.calls` was the "draw calls climb forever" bug).
 *    Sampling `info` on a timer instead reads whatever the LAST pass left behind,
 *    which for a postprocessed scene is the blit's single draw call rather than
 *    the hundreds that drew the scene.
 *
 * 2. **There is no real frame boundary to sum over.** `RenderingGraph` completes
 *    a sequence per `render()` call, and a postprocessing pipeline runs several
 *    of those per displayed frame (see renderGraph.ts). Nothing hooks the host
 *    app's rAF. So we accumulate over a rolling window and divide by the frames
 *    measured in it — an average per frame, which is also steadier to read than
 *    a single frame's spike.
 */

export interface RenderPassStats {
    readonly drawCalls: number;
    readonly triangles: number;
}

export type RendererBackend = "webgl" | "webgpu" | "unknown";

/**
 * A renderer's `renderer.info.render` normalised across three's two backends, so
 * NO caller ever touches the raw fields — which mean genuinely different things:
 *
 * - **WebGLRenderer** (`WebGLInfo`): `render.calls` is DRAW calls. It is per-pass
 *   with the default `autoReset`; with `autoReset === false`, calls and triangles
 *   are cumulative. Frame counter at `render.frame`.
 * - **WebGPURenderer** (`Info`): `render.drawCalls`/`triangles` accumulate across
 *   the frame's passes and reset per frame; `render.calls` is LIFETIME render()
 *   calls; `render.frameCalls` is render() calls this frame. Frame counter at `info.frame`.
 *
 * Backend detection and that mapping live ONLY in {@link readRendererInfo}.
 */
export interface NormalizedRenderInfo {
    readonly backend: RendererBackend;
    /** Draw calls the renderer currently reports. `drawCallsScope` gives its span. */
    readonly drawCalls: number;
    /** Triangles the renderer currently reports (same span as `drawCalls`). */
    readonly triangles: number;
    /** Whether `drawCalls`/`triangles` cover a frame, one pass, or the renderer's
     * cumulative history. */
    readonly drawCallsScope: "frame" | "pass" | "cumulative";
    /** render() invocations in the current frame (WebGPU). undefined on WebGL. */
    readonly renderCallsThisFrame?: number;
    /** Lifetime render() invocations (WebGPU). undefined on WebGL. */
    readonly renderCallsTotal?: number;
    /** The renderer's frame counter, if it exposes one. */
    readonly frameId?: number;
}

interface RawRenderInfo { calls?: number; drawCalls?: number; frameCalls?: number; triangles?: number; frame?: number }

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/**
 * Read + normalise `renderer.info`. The ONLY place that knows the WebGL/WebGPU
 * field differences. Returns null when the renderer exposes no info.
 */
export function readRendererInfo(renderer: unknown): NormalizedRenderInfo | null {
    const info = (renderer as { info?: { render?: RawRenderInfo; frame?: number; autoReset?: boolean } } | null | undefined)?.info;
    const r = info?.render;
    if (!r) return null;

    // WebGPU exposes per-frame drawCalls + frameCalls; WebGL has neither.
    if (typeof r.drawCalls === "number") {
        return {
            backend: "webgpu",
            drawCalls: num(r.drawCalls),
            triangles: num(r.triangles),
            drawCallsScope: "frame",
            renderCallsThisFrame: typeof r.frameCalls === "number" ? r.frameCalls : undefined,
            renderCallsTotal: typeof r.calls === "number" ? r.calls : undefined,
            frameId: typeof info?.frame === "number" ? info.frame : undefined,
        };
    }
    return {
        backend: "webgl",
        drawCalls: num(r.calls),
        triangles: num(r.triangles),
        drawCallsScope: info.autoReset === false ? "cumulative" : "pass",
        frameId: typeof r.frame === "number" ? r.frame : undefined,
    };
}

/** Compiled shader programs, and their bytes where the backend reports them. */
export interface ProgramInfo {
    /** compiled programs currently alive */
    readonly count: number;
    /** their size in bytes — WebGPU only; WebGL does not report it */
    readonly bytes?: number;
}

/**
 * Read the compiled shader program count. Null when the renderer says nothing.
 *
 * Worth having per second rather than once: the FIRST render of a program
 * compiles it, which stalls the frame, so a count that steps UP mid-session is a
 * compile that just happened — the usual explanation for "it hitched the first
 * time I hovered that thing".
 *
 * The two backends disagree in shape (three's own source):
 * - WebGL: `info.programs` is the program CACHE ARRAY (WebGLInfo.js:64)
 * - WebGPU: `info.memory.programs` is a COUNT, with `programsSize` in bytes
 *   (renderers/common/Info.js:104-126)
 */
export function readProgramInfo(renderer: unknown): ProgramInfo | null {
    const info = (renderer as { info?: { programs?: unknown; memory?: { programs?: unknown; programsSize?: unknown } } } | null | undefined)?.info;
    if (!info) return null;

    if (Array.isArray(info.programs)) return { count: info.programs.length };

    const memory = info.memory;
    if (typeof memory?.programs === "number" && Number.isFinite(memory.programs)) {
        return typeof memory.programsSize === "number" && Number.isFinite(memory.programsSize)
            ? { count: memory.programs, bytes: memory.programsSize }
            : { count: memory.programs };
    }
    return null;
}

/** Per-renderer carry for counters that must be differenced into per-pass values.
 *  One per renderer, threaded through {@link readPassStats}. */
export interface RenderInfoCursor { drawCalls: number; triangles: number; frameCalls: number; cumulativeInitialized: boolean; }
export function newRenderInfoCursor(): RenderInfoCursor { return { drawCalls: 0, triangles: 0, frameCalls: 0, cumulativeInitialized: false }; }

/**
 * One pass's draw calls / triangles from a {@link NormalizedRenderInfo} read at the
 * END of that pass, so the caller can keep SUMMING passes into a frame total:
 *
 * - **pass scope** (default WebGL): the value is already THIS pass.
 * - **cumulative scope** (`WebGLInfo.autoReset === false`): subtract the previous
 *   observation without counting work that happened before inspection began.
 * - **frame scope** (WebGPU): the counters accumulate across the frame, so this
 *   pass = the delta since the previous pass; a new frame restarts from 0, detected
 *   by `renderCallsThisFrame` resetting (raw count-drop fallback when it's absent).
 */
export function readPassStats(info: NormalizedRenderInfo | null | undefined, cursor: RenderInfoCursor): RenderPassStats {
    if (!info) return { drawCalls: 0, triangles: 0 };

    if (info.drawCallsScope === "pass") {
        return { drawCalls: info.drawCalls, triangles: info.triangles };
    }

    if (info.drawCallsScope === "cumulative") {
        const initialized = cursor.cumulativeInitialized;
        const drawCalls = initialized
            ? (info.drawCalls >= cursor.drawCalls ? info.drawCalls - cursor.drawCalls : info.drawCalls)
            : 0;
        const triangles = initialized
            ? (info.triangles >= cursor.triangles ? info.triangles - cursor.triangles : info.triangles)
            : 0;
        cursor.drawCalls = info.drawCalls;
        cursor.triangles = info.triangles;
        cursor.cumulativeInitialized = true;
        return { drawCalls, triangles };
    }

    const newFrame = info.renderCallsThisFrame !== undefined
        ? info.renderCallsThisFrame <= cursor.frameCalls
        : info.drawCalls < cursor.drawCalls;

    const drawCalls = Math.max(0, info.drawCalls - (newFrame ? 0 : cursor.drawCalls));
    const triangles = Math.max(0, info.triangles - (newFrame ? 0 : cursor.triangles));

    cursor.drawCalls = info.drawCalls;
    cursor.triangles = info.triangles;
    cursor.frameCalls = info.renderCallsThisFrame ?? 0;
    return { drawCalls, triangles };
}

/**
 * Backend-agnostic render numbers, read at the end of each pass. Every field means
 * ONE thing regardless of renderer; where a backend can't supply it, the value is
 * `undefined` (never a mislabelled counter). See {@link RenderInfoReader}.
 */
export interface RenderInfoStats {
    readonly backend: RendererBackend;
    /** Draw calls / triangles in the current displayed frame. WebGPU: native.
     *  WebGL: summed from the frame's passes — `undefined` until the first frame
     *  boundary is seen. */
    readonly drawCallsThisFrame?: number;
    readonly trianglesThisFrame?: number;
    /** Lifetime draw calls / triangles. No backend exposes a lifetime DRAW-call
     *  counter (WebGPU's `calls` is RENDER calls), so these are running sums the
     *  reader keeps — always present. */
    readonly drawCallsTotal: number;
    readonly trianglesTotal: number;
    /** render() invocations in the current frame (WebGPU native; WebGL: passes
     *  counted per frame). */
    readonly renderCallsThisFrame?: number;
    /** Lifetime render() invocations (WebGPU native; WebGL has no such counter). */
    readonly renderCallsTotal?: number;
    readonly frameId?: number;
    /** This pass's draw calls / triangles — what {@link RenderStatsAccumulator} sums. */
    readonly thisPass: RenderPassStats;
}

/** Sums a WebGL renderer's per-pass work into displayed frames. WebGL resets `info`
 *  every `render()`, so it has no per-frame total; the caller marks frame boundaries
 *  with a monotonic id (a rAF tick), and we expose the LAST COMPLETE frame. */
class DisplayedFrameGrouper {
    private currentFrame = -1;
    private draws = 0;
    private tris = 0;
    private passes = 0;
    private lastDraws?: number;
    private lastTris?: number;
    private lastPasses?: number;

    record(displayedFrame: number, pass: RenderPassStats): { drawCalls?: number; triangles?: number; passes?: number } {
        if (displayedFrame !== this.currentFrame) {
            if (this.currentFrame >= 0) { this.lastDraws = this.draws; this.lastTris = this.tris; this.lastPasses = this.passes; }
            this.draws = 0; this.tris = 0; this.passes = 0;
            this.currentFrame = displayedFrame;
        }
        this.draws += pass.drawCalls;
        this.tris += pass.triangles;
        this.passes += 1;
        return { drawCalls: this.lastDraws, triangles: this.lastTris, passes: this.lastPasses };
    }
}

/**
 * Stateful, per-renderer. Fed one pass at a time (`read` at the end of each pass),
 * it produces the uniform {@link RenderInfoStats} — hiding the WebGL/WebGPU split
 * behind honest, single-meaning fields, computing what no backend hands us
 * (lifetime totals; WebGL's per-frame totals). Hold one per renderer.
 */
export class RenderInfoReader {
    private readonly cursor = newRenderInfoCursor();
    private readonly grouper = new DisplayedFrameGrouper();
    private drawCallsTotal = 0;
    private trianglesTotal = 0;

    /** `displayedFrame` is a monotonic id that bumps once per displayed frame (the
     *  boundary WebGL's own `info` lacks). Ignored for WebGPU (it reports per-frame
     *  natively). Returns null when the renderer exposes no info. */
    read(renderer: unknown, displayedFrame: number): RenderInfoStats | null {
        const norm = readRendererInfo(renderer);
        if (!norm) return null;

        const thisPass = readPassStats(norm, this.cursor);
        this.drawCallsTotal += thisPass.drawCalls;
        this.trianglesTotal += thisPass.triangles;

        let drawCallsThisFrame: number | undefined;
        let trianglesThisFrame: number | undefined;
        let renderCallsThisFrame: number | undefined;
        if (norm.drawCallsScope === "frame") {
            // WebGPU: the counters ARE the running frame total.
            drawCallsThisFrame = norm.drawCalls;
            trianglesThisFrame = norm.triangles;
            renderCallsThisFrame = norm.renderCallsThisFrame;
        } else {
            // WebGL: sum this frame's passes ourselves (one-frame lag).
            const frame = this.grouper.record(displayedFrame, thisPass);
            drawCallsThisFrame = frame.drawCalls;
            trianglesThisFrame = frame.triangles;
            renderCallsThisFrame = frame.passes;
        }

        return {
            backend: norm.backend,
            drawCallsThisFrame,
            trianglesThisFrame,
            drawCallsTotal: this.drawCallsTotal,
            trianglesTotal: this.trianglesTotal,
            renderCallsThisFrame,
            renderCallsTotal: norm.renderCallsTotal,
            frameId: norm.frameId,
            thisPass,
        };
    }
}

export interface RenderStatsSummary {
    /** average draw calls per displayed frame */
    readonly drawCalls: number;
    /** average triangles per displayed frame */
    readonly triangles: number;
    /** average `render()` invocations per displayed frame — shadow, scene, post… */
    readonly passes: number;
}

interface WindowEntry extends RenderPassStats {
    readonly at: number;
}

const usable = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export class RenderStatsAccumulator {

    private entries: WindowEntry[] = [];

    /** One completed render pass, with what `renderer.info` reported for it. */
    addPass(at: number, stats: Partial<RenderPassStats>): void {
        const drawCalls = usable(stats.drawCalls) ? stats.drawCalls : 0;
        const triangles = usable(stats.triangles) ? stats.triangles : 0;
        // a pass that reported nothing (a renderer without info) would still count
        // as a pass and drag the average down — skip it entirely
        if (drawCalls <= 0 && triangles <= 0) return;
        this.entries.push({ at, drawCalls, triangles });
    }

    /**
     * The average frame over the window, or null when nothing rendered in it —
     * a still editor should not keep showing a number from a minute ago.
     */
    summarize(now: number, opts: { windowMs: number, frames: number }): RenderStatsSummary | null {
        const cutoff = now - opts.windowMs;
        // trim here rather than on every add: this runs once per UI tick
        if (this.entries.length > 0 && this.entries[0].at < cutoff) {
            this.entries = this.entries.filter(entry => entry.at >= cutoff);
        }
        if (this.entries.length === 0) return null;

        const frames = Math.max(1, Math.round(opts.frames));
        let drawCalls = 0, triangles = 0;
        for (const entry of this.entries) {
            drawCalls += entry.drawCalls;
            triangles += entry.triangles;
        }
        return {
            drawCalls: Math.round(drawCalls / frames),
            triangles: Math.round(triangles / frames),
            passes: Math.round(this.entries.length / frames),
        };
    }

    clear(): void {
        this.entries = [];
    }
}
